// CheckoutPage.jsx — calls POST /api/auth/create-checkout-session
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const D = {
  bg: "#0E0F14", surface: "#13151C", card: "#191C26",
  border: "rgba(255,255,255,0.07)", accentBorder: "rgba(108,126,248,0.25)",
  accent: "#6C7EF8", accentDim: "rgba(108,126,248,0.12)",
  success: "#34D399", danger: "#F87171",
  textPrimary: "#F0F2F7", textSec: "#B0B7CE", textMuted: "#5A607A",
  fontDisplay: "'Plus Jakarta Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

const INCLUDES = [
  "10 trend intelligence searches per month",
  "4 full content generations per month",
  "Facebook, Instagram, LinkedIn, Twitter/X, Blog, Newsletter",
  "AI-generated post images — every content set",
  "Cited sources packaged with every output",
  "16-signal composite opportunity scoring",
  "Brand voice calibration & guardrails",
  "ZIP export — posts, images, citations together",
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const [errMsg, setErrMsg] = useState("");

  const email = localStorage.getItem("email");

  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  const handleCheckout = async () => {
    setStatus("loading");
    setErrMsg("");
    try {
      const storedProfile = localStorage.getItem("checkout_profile");
      const brandProfile = storedProfile ? JSON.parse(storedProfile) : {};

      const res = await fetch(`${API}/api/auth/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          brand_profile: brandProfile,
          price_id: import.meta.env.VITE_STRIPE_PRICE_ID || "",
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Server error ${res.status}`);
      }

      const { checkout_url } = await res.json();
      window.location.href = checkout_url;
    } catch (e) {
      console.error("[Checkout]", e);
      setStatus("error");
      setErrMsg(e.message || "Failed to start checkout. Please try again.");
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${D.bg}; color: ${D.textPrimary}; font-family: ${D.fontBody}; -webkit-font-smoothing: antialiased; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .fade-up { animation: fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }
        .d1 { animation-delay: 0.08s; }
        .d2 { animation-delay: 0.16s; }
        .page-glow { position:fixed; inset:0; z-index:0; pointer-events:none; background: radial-gradient(ellipse 60% 50% at 50% -10%, rgba(108,126,248,0.08) 0%, transparent 60%); }
        @media (max-width: 600px) {
          .checkout-stepper { display: none !important; }
          .checkout-nav-spacer { display: none !important; }
        }
      `}</style>

      <div className="page-glow" />

      {/* NAV */}
      <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:100, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 32px", height:60, background:"rgba(14,15,20,0.9)", backdropFilter:"blur(16px)", borderBottom:`1px solid ${D.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }} onClick={() => navigate("/")}>
          <div style={{ width:30, height:30, borderRadius:8, background:`linear-gradient(135deg, ${D.accent} 0%, #9B6EF8 100%)`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:D.fontDisplay, fontWeight:800, fontSize:14, color:"white" }}>Z</div>
          <span style={{ fontFamily:D.fontDisplay, fontWeight:700, fontSize:15, color:D.textPrimary }}>zenithpost<span style={{ color:D.accent }}>.ai</span></span>
        </div>
        <div className="checkout-stepper" style={{ display:"flex", alignItems:"center", gap:6 }}>
          {["Account", "Brand Profile", "Checkout"].map((s, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
              {i > 0 && <div style={{ width:20, height:1, background:D.border }} />}
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:20, height:20, borderRadius:"50%", background:i===2?D.accent:i<2?D.success:"rgba(255,255,255,0.06)", border:`1px solid ${i===2?D.accent:i<2?D.success:D.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:D.fontMono, fontSize:10, color:"white", fontWeight:600 }}>{i < 2 ? "✓" : "3"}</div>
                <span style={{ fontFamily:D.fontBody, fontSize:12, color:i===2?D.textPrimary:D.textMuted, fontWeight:i===2?600:400 }}>{s}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="checkout-nav-spacer" style={{ width:120 }} />
      </div>

      {/* MAIN */}
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:"80px 20px 40px", position:"relative", zIndex:1 }}>
        <div style={{ width:"100%", maxWidth:480 }}>

          <div className="fade-up" style={{ textAlign:"center", marginBottom:32 }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"4px 14px", borderRadius:99, background:D.accentDim, border:`1px solid ${D.accentBorder}`, fontFamily:D.fontMono, fontSize:10.5, color:D.accent, marginBottom:16 }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:D.accent, animation:"pulse 1.8s ease infinite", display:"inline-block" }} />
              Secured by Stripe
            </div>
            <h1 style={{ fontFamily:D.fontDisplay, fontWeight:800, fontSize:30, color:D.textPrimary, lineHeight:1.15, marginBottom:10 }}>Complete your subscription</h1>
            <p style={{ fontSize:14.5, color:D.textSec, lineHeight:1.65 }}>One final step to activate your zenithpost.ai account. Your brand profile is saved and ready.</p>
          </div>

          <div className="fade-up d1" style={{ background:D.card, border:`1px solid ${D.border}`, borderRadius:20, overflow:"hidden", boxShadow:"0 24px 64px rgba(0,0,0,0.4)" }}>
            <div style={{ background:"linear-gradient(135deg, #0D0D12 0%, #1A1A2E 100%)", padding:"28px 32px", position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 80% 50%, rgba(108,126,248,0.25) 0%, transparent 60%)", pointerEvents:"none" }} />
              <div style={{ fontFamily:D.fontMono, fontSize:10.5, color:"rgba(255,255,255,0.4)", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8 }}>zenithpost.ai Standard</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:4 }}>
                <span style={{ fontFamily:D.fontDisplay, fontWeight:800, fontSize:40, color:"white", lineHeight:1 }}>$48.00</span>
                <span style={{ fontFamily:D.fontBody, fontSize:14, color:"rgba(255,255,255,0.5)" }}>/month</span>
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", fontFamily:D.fontBody }}>Billed monthly · Cancel anytime · No contracts</div>
            </div>

            <div style={{ padding:"24px 32px 28px" }}>
              <div style={{ fontFamily:D.fontMono, fontSize:10, color:D.textMuted, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:14 }}>What's included</div>
              <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:10, marginBottom:24 }}>
                {INCLUDES.map((f, i) => (
                  <li key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, fontSize:13.5, color:D.textSec, lineHeight:1.5 }}>
                    <span style={{ color:D.success, flexShrink:0, fontSize:14, marginTop:1 }}>✓</span>{f}
                  </li>
                ))}
              </ul>

              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:10, background:"rgba(255,255,255,0.03)", border:`1px solid ${D.border}`, marginBottom:20 }}>
                <div style={{ width:28, height:28, borderRadius:"50%", background:D.accentDim, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, flexShrink:0 }}>👤</div>
                <div>
                  <div style={{ fontFamily:D.fontMono, fontSize:10, color:D.textMuted, letterSpacing:"0.06em", textTransform:"uppercase" }}>Signing up as</div>
                  <div style={{ fontSize:13.5, color:D.textPrimary, fontWeight:600, marginTop:2 }}>{email || "—"}</div>
                </div>
              </div>

              {status === "error" && (
                <div style={{ display:"flex", gap:10, padding:"12px 14px", borderRadius:10, background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.2)", marginBottom:16 }}>
                  <span style={{ fontSize:14, flexShrink:0 }}>⚠</span>
                  <span style={{ fontSize:13, color:"#F8A0A0", lineHeight:1.55 }}>{errMsg}</span>
                </div>
              )}

              <button disabled={status === "loading"} onClick={handleCheckout} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, padding:"14px 24px", borderRadius:12, border:"none", cursor:status==="loading"?"not-allowed":"pointer", background:status==="loading"?"rgba(108,126,248,0.5)":`linear-gradient(135deg, ${D.accent} 0%, #8B6EF8 100%)`, fontFamily:D.fontBody, fontSize:15, fontWeight:700, color:"white", boxShadow:status==="loading"?"none":"0 6px 24px rgba(108,126,248,0.35)", opacity:status==="loading"?0.7:1 }}>
                {status === "loading" ? (
                  <><div style={{ width:16, height:16, borderRadius:"50%", border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"white", animation:"spin 0.7s linear infinite" }} />Redirecting to Stripe…</>
                ) : "Continue to secure payment →"}
              </button>

              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:14 }}>
                <span style={{ fontSize:13, color:D.textMuted }}>🔒</span>
                <span style={{ fontFamily:D.fontMono, fontSize:10.5, color:D.textMuted }}>256-bit SSL · Powered by Stripe · We never store card details</span>
              </div>
            </div>
          </div>

          <div className="fade-up d2" style={{ textAlign:"center", marginTop:20 }}>
            <button onClick={() => navigate("/brand-profile")} style={{ background:"none", border:"none", cursor:"pointer", fontFamily:D.fontBody, fontSize:13.5, color:D.textMuted }}>
              ← Back to brand profile
            </button>
          </div>
        </div>
      </div>
    </>
  );
}